import { motion, type Transition, type Variants } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import { Button } from '@highlight/ui'
import { SuccessHalftone } from '@renderer/components/success-halftone'

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 12, filter: 'blur(4px)' },
  visible: { opacity: 1, y: 0, filter: 'blur(0px)' },
}

const cardTransition: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4,
}

interface TourPromptCardProps {
  onAccept: () => void
  onDecline: () => void
}

/**
 * The opt-in offer that precedes the tour. Deliberately not a tour step: the
 * tour is modal, and an invitation the user can't ignore isn't an invitation.
 *
 * Rendered into the chat input's `before` slot, in flow above the input,
 * alongside the permission toggles and the meeting-mode notice — the same
 * arrangement, and the reason it isn't positioned against the view: the footer
 * is opaque chrome drawn over the view's own bottom edge, so a card floating
 * there is painted over by the composer and the floating bar.
 *
 * Carries the onboarding halftone's surge as a first-meeting celebration. It
 * plays once per mount, and the card only mounts when the offer appears, so
 * the beat lands with the offer rather than on every render of the view.
 */
export function TourPromptCard({ onAccept, onDecline }: TourPromptCardProps) {
  return (
    <motion.div
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={cardTransition}
      role="dialog"
      aria-label="Take a tour of your meeting notes"
      className="bg-background corner-smooth relative mb-3 flex flex-col gap-3 overflow-hidden rounded-2xl border p-3"
    >
      {/* Brand yellow and the warmer peak of the curating screen's field —
          the onboarding halftone reads as a celebration in that colour, and
          the success green it defaults to reads as a status instead. */}
      <SuccessHalftone
        colorVariable="--brand"
        peakAlphaLight={0.35}
        peakAlphaDark={0.22}
        // Rises from behind the primary action rather than the card's middle.
        origin="bottom-right"
      />
      {/* Above the wash, which fills the card behind it. */}
      <div className="relative flex items-start gap-2.5">
        <Sparkles aria-hidden="true" className="text-foreground-secondary mt-0.5 size-3.5 shrink-0" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <h3 className="text-foreground type-ui-sm font-medium">You just finished your first meeting</h3>
          <p className="text-foreground-secondary type-prose-sm">Want a quick tour of everything Highlight captured?</p>
        </div>
      </div>
      <div className="relative flex items-center justify-end gap-1.5">
        <Button variant="outline" onClick={onDecline}>
          No thanks
        </Button>
        <Button variant="primary" onClick={onAccept}>
          Show me around
        </Button>
      </div>
    </motion.div>
  )
}
